import React, { useEffect, useState } from 'react'
import ServiceLocator from 'live/services/service-locator'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import GlobalAlert from 'live/components/ui/global-alert'

type ExtraActionsInjectedProps = {
    readonly: boolean
    renderSeparator: boolean
    afterActionExecute: (...any) => void
}

const ExtraActionsInjected: React.FC<ExtraActionsInjectedProps> = ({
    readonly,
    renderSeparator,
    afterActionExecute
}: ExtraActionsInjectedProps) => {
    const [actionsRegistry, setActionsRegistry] = useState(null)
    const [showLoadingExtraAction, setShowLoadingExtraAction] = useState<boolean>(false)
    const [showLoadingExtraActionLabel, setShowLoadingExtraActionLabel] = useState<string>('<none>')

    useEffect(() => {
        ServiceLocator.getService('purge-extra-actions-registry').then(setActionsRegistry)
    }, [])

    const disabled = readonly || showLoadingExtraAction

    return actionsRegistry ? (
        <actionsRegistry.Consumer>
            {actions => (
                <>
                    {actions.length > 0 && renderSeparator && <VerticalLine />}
                    <GlobalAlert
                        show={showLoadingExtraAction}
                        neverClose={false}
                        allowClose={false}
                        visibleTime={0}
                        message={i18n('PerformExtraAction', { label: showLoadingExtraActionLabel })}
                        className={`alert-sm info`}
                    />
                    {actions.length > 0 && (
                        <ExtraActionsWrapper>
                            {actions.map((actionEntry, index) => {
                                return (
                                    <ExtraButton
                                        key={index}
                                        disabled={disabled}
                                        onClick={async () => {
                                            let success = false
                                            let message = ''
                                            try {
                                                setShowLoadingExtraActionLabel(actionEntry?.label)
                                                setShowLoadingExtraAction(true)
                                                await actionEntry.callback()
                                                success = true
                                                message = actionEntry.successMessage
                                            } catch (e) {
                                                success = false
                                                message = actionEntry.errorMessage
                                            } finally {
                                                setShowLoadingExtraAction(false)
                                                afterActionExecute(success, message)
                                            }
                                        }}
                                    >
                                        <i className={`far fa-regular ${actionEntry.icon}`}></i>
                                        {actionEntry?.label}
                                    </ExtraButton>
                                )
                            })}
                        </ExtraActionsWrapper>
                    )}
                </>
            )}
        </actionsRegistry.Consumer>
    ) : null
}

const VerticalLine = styled.div`
    border-left: 1px solid var(--gray-400);
    margin: 0 10px;
`

const ExtraActionsWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    flex-grow: 1;
    max-height: 155px;
    overflow: auto;
    gap: 8px;

    &::-webkit-scrollbar {
        width: 5px;
    }

    &::-webkit-scrollbar-thumb {
        background: var(--gray-600);
        border-radius: 0;
        background-clip: padding-box;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
        border-radius: 0;
        margin: 1px;
    }
`

const ExtraButton = styled.button`
    background: ${({ disabled }) => (disabled ? 'var(--gray-200)' : 'var(--yellow-700)')};
    color: ${({ disabled }) => (disabled ? 'var(--gray-700)' : 'var(--white)')};

    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 12px 24px;
    gap: 8px;

    height: 40px;

    border: none;
    border-radius: 3px;
    font-size: 16px;
    font-weight: 600;

    flex: none;
    order: 0;
    flex-grow: 0;
`

export default ExtraActionsInjected
